import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canOpenPage } from '../utils/pagePermissions';

const FULL_ACCESS_ROLES = ['super_admin', 'system_admin'];

export const AccessDenied = () => (
  <div className="min-h-screen flex items-center justify-center bg-secondary-offWhite">
    <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
      <div className="text-red-600 text-5xl mb-4">🚫</div>
      <h2 className="text-2xl font-bold text-primary-black mb-2">Access Denied</h2>
      <p className="text-primary-slate mb-4">You don't have permission to access this page.</p>
      <button
        onClick={() => window.history.back()}
        className="px-6 py-2 bg-accent-gold text-primary-black rounded-lg font-semibold hover:bg-accent-goldDark transition-colors"
      >
        Go Back
      </button>
    </div>
  </div>
);

/**
 * Page guard. The role's permissions (Roles & Permissions on /users) decide access for pages
 * covered by utils/pagePermissions; allowedRoles is the fallback for other pages and for
 * users whose permissions are unknown. Pages reserved for super/system admins stay reserved.
 */
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-offWhite">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
          <p className="mt-4 text-primary-slate">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isReservedPage =
    allowedRoles.length > 0 && allowedRoles.every((role) => FULL_ACCESS_ROLES.includes(role));
  const pageAccess = isReservedPage ? null : canOpenPage(user, location.pathname);

  if (pageAccess === false) {
    return <AccessDenied />;
  }

  // Check role-based access
  if (pageAccess === null && allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <AccessDenied />;
  }

  return children;
};

export default ProtectedRoute;
